/**
 * The browser half of the sync engine.
 *
 * It owns one WebSocket, one outbox, and the *confirmed* server state that
 * `reconcile.ts` describes. Everything else in the app keeps talking to
 * {@link WorkspaceStore} exactly as it did in M2 — the client attaches to the
 * store's `observeCommands` seam and its `replaceState`, and never replaces it.
 *
 * ```
 * dispatch ─▶ store (optimistic) ─▶ observeCommands ─▶ outbox (IndexedDB) ─▶ push
 *                     ▲                                                       │
 *                     └──────── replaceState(replay(confirmed, outbox)) ◀──── ack /
 *                                                                    commands /
 *                                                                    snapshot
 * ```
 *
 * ## Connect, catch up, flush — in that order
 *
 * On every connect the client sends `pull(currentVersion)` and waits for the
 * server's answer before flushing the outbox. `currentVersion` is `0` on a
 * fresh page load, so the first connect of a session re-establishes the server
 * prefix from scratch (a snapshot, or the whole log); a reconnect after a
 * dropped socket asks only for what it missed.
 *
 * The confirmed state is deliberately **not persisted**. It is cheap to
 * re-establish, and not persisting it removes the only way the client could
 * boot believing a prefix it does not actually have. What *is* persisted is the
 * optimistic snapshot (M2's `persistence.ts`, so a reload paints instantly and
 * offline) and the outbox (so nothing is lost).
 *
 * ## When the screen is recomputed
 *
 * `replaceState` clears the undo stack — correctly, since stacked inverses were
 * computed against a state that no longer exists — so it is called only when
 * the command *sequence* actually changed: a remote delta, a snapshot, or a
 * command dropped from the outbox. An ack that merely moves the client's own
 * commands from "unacked" to "confirmed" leaves `replay(confirmed, outbox)`
 * mathematically identical to what is already on screen, so nothing is touched
 * and undo survives ordinary syncing.
 *
 * A live delta — a `commands` frame reaching a client that is already caught
 * up — also names exactly what changed, so it is handed to `replaceState` as
 * `over` and the store keeps every entry those commands do not touch
 * (PLAN-COLLAB D-CO10). Only that frame, and only when it is the whole story:
 * a catch-up can span pages of which only the last recomputes, a snapshot names
 * nothing, and a recompute that drops one of our own queued commands changed
 * our state in a way no stacked inverse saw. All of those still clear.
 *
 * The **first** catch-up of a session is the exception, and it recomputes
 * whatever the server said. The rule above assumes the screen already holds
 * `replay(confirmed, pending)`, and on a boot that is an assumption about a
 * store somebody else filled: `workspace-context.tsx` opens an *empty*
 * workspace whenever its IndexedDB read loses the hydration race, while the
 * confirmed prefix resumes from disk at the server's head — so "nothing new"
 * would leave a brewer looking at an empty workspace the client was itself
 * holding. The boot recompute compares by value first and skips `replaceState`
 * when the two already agree, so the ordinary reload still keeps its undo stack.
 *
 * ## The window between "on screen" and "on disk"
 *
 * `observeCommands` fires synchronously, but the outbox is IndexedDB and its
 * append resolves a turn or more later. That gap is a real hazard, because the
 * diagram above only holds if `#pending` is the *whole* outbox: the instant a
 * dispatch has been applied optimistically but is not yet in `#pending`, any
 * sequence-changing frame — a reconnect snapshot, another device's delta, an
 * assistant changeset — would recompute `replay(confirmed, #pending)` without
 * it and paint the command's effect straight off the screen. It would come back
 * only at the *next* such frame, since an all-accepted ack deliberately does not
 * recompute. "The hop I just added vanished and reappeared a minute later" is
 * exactly that shape.
 *
 * So `#pending` is ordered by **dispatch**, not by durability: an entry joins it
 * synchronously in `#onLocalCommand`, carrying `key: null` until its write
 * commits. Two consequences follow, and both are load-bearing:
 *
 * - Every reader of `#pending` — above all `#recompute` — sees the command from
 *   the moment the user caused it. That is the invariant the whole file rests on.
 * - `#push` may only send a **durable prefix**, since a command that has not
 *   reached disk cannot survive the crash the outbox exists to survive. It stops
 *   at the first entry whose write is still in flight rather than stepping over
 *   it, because sending a later command before an earlier one would reorder the
 *   log.
 *
 * ## An unsaved entry
 *
 * A write can also *fail*. It is retried once a second later, because a quota
 * error is often transient, and if the second attempt rejects too the entry is
 * marked **unsaved**: still on screen, still in `#pending`, still in its place
 * in the queue — and pushed, which is the part that needs saying. The durable
 * prefix rule stops at a write that has not answered yet, because that write is
 * about to answer; it does not stop at one that has failed, because waiting for
 * a key that is never coming is how a single rejected append used to wedge
 * every command dispatched behind it, with nothing scheduled to move any of
 * them and nothing but a reload to escape.
 *
 * Pushing it is a trade, and the honest way to describe it is that the server
 * becomes the command's only copy. At-most-once beats certain loss: on the disk
 * it is nowhere, on the server it is everywhere this account can read it from.
 * The residual is one case, and it is inherent to that device rather than to
 * this client — storage that refuses writes **and** a network that is gone, with
 * the page closing before either recovers, loses that edit. `onDurabilityFailure`
 * fires the moment the second write fails, so the failure is reported when it
 * happens rather than by the reload that notices it is gone.
 *
 * ## Guest data on first sign-in
 *
 * Two cases, and no merge:
 *
 * - **Server workspace empty** (`version === 0`): the guest workspace is
 *   uploaded as a batch of `recipe.restore` / `equipment.restore` commands.
 *   Nothing can be lost, so it happens automatically.
 * - **Server workspace not empty**: the server wins on screen. The guest
 *   workspace is *not* touched — it lives under its own IndexedDB key
 *   (`snapshot.vN`, versus `snapshot.vN.<userId>` for a signed-in user) and
 *   stays there, downloadable from Settings. `onGuestConflict` fires so the UI
 *   can say so.
 *
 * ## Reconnect
 *
 * Exponential backoff with jitter, reset on a successful open. Jitter matters
 * even for a single-user app: a laptop waking from sleep reconnects every tab
 * at once.
 *
 * The reads in front of the socket are inside that loop rather than in front of
 * it. `start()` is `connecting` from its first line, and the outbox and the
 * kept prefix it loads before connecting can fail — `OPEN_TIMEOUT_MS` rejects
 * an `indexedDB.open()` that a backgrounded Android tab left stalled. A
 * rejection there used to leave the client in `connecting` forever with no
 * socket and nothing scheduled, which is the one status that promises something
 * is still coming. So a failed read is `offline` and a scheduled retry like any
 * other failed attempt, and the retry re-runs the *reads* until they succeed —
 * `idb.ts` drops a rejected open from its memo, so the next attempt opens fresh
 * rather than inheriting the stall.
 *
 * ## Nothing waits forever
 *
 * Every state above is entered by sending something and left by receiving
 * something, so a socket that stops delivering leaves the client waiting in it
 * with nothing scheduled — and "Syncing" that means forever is a lie the
 * indicator used to be able to tell. One rule closes that: **an outbound
 * message must be answered within `REPLY_DEADLINE_MS`, and a message that is
 * not answered means the socket is dead.** Any frame at all clears the
 * deadline; `#onFrame` re-arms it while anything is still outstanding; when it
 * fires, {@link SyncClient.reconnect} throws the socket away and opens another,
 * which replays the outbox exactly as a dropped connection does.
 *
 * The deadline **lengthens while it keeps being wrong**. A link slow enough
 * that a round trip takes longer than fifteen seconds is not a dead one, and a
 * fixed bound turns it into deadlock: every catch-up is abandoned a moment
 * before its answer crosses, the reconnect asks the same question again, and
 * the client makes no progress for as long as the network is bad. So a
 * deadline that fires on a socket that had *opened* doubles the wait for the
 * next one, up to eight times the base.
 *
 * What brings it back down is a **prompt** answer, not any answer: a frame
 * resets the deadline to the base only if it arrived within one base deadline
 * of the wait starting. That distinction is the whole rule. An answer that took
 * twelve seconds on a fifteen-second base is the link saying it still needs the
 * patience it was given, and handing it straight back means the *next* message
 * is set up to fail — the catch-up crosses inside a doubled deadline, its frame
 * resets the client to the base, and the push behind it is dropped with the
 * socket before it can climb. Patience is earned by silence and spent by a
 * reply that was quick, which is the same thing as saying the client believes
 * what the link last showed it.
 *
 * What none of this lengthens is the first reconnect of a healthy client:
 * half-open detection is fifteen seconds on the first hit, always, and a link
 * that is genuinely dead fails its next *open* and goes through the ordinary
 * backoff rather than through the deadline at all. `chaos/bad-network.test.ts`
 * plays out both ends of it.
 *
 * A quiet socket is asked directly, once every `HEARTBEAT_MS`, whenever the tab
 * comes back to the front, and whenever the browser says the network is back,
 * with the one frame that is not a message. Those last two are the same event
 * seen from two sides — a phone that changed networks fires `online` and no
 * `close` — so both ask the socket they have rather than assuming it is fine
 * or assuming it is gone. Edits are safe throughout all of this: they apply
 * locally and sit in the outbox. Only the connection is ever stuck, and this
 * is how it gets noticed.
 *
 * ## A shared recipe's room
 *
 * The same client talks to a room (PLAN-COLLAB C1) with nothing above changed:
 * a room speaks the workspace's protocol, and a store per room is a store like
 * any other. What a room adds is three frames' worth of difference, each taken
 * by an option rather than by a branch in the machinery:
 *
 * - **A 4404 close** ({@link ROOM_CLOSED_CODE}) is the room saying it will
 *   never answer this member again — never made, wiped, or they were removed.
 *   The client stops as `stop()` stops, refuses every later `start()`, and
 *   calls `onGone`. Nothing reconnects, because every attempt would be the
 *   same close, and nothing reports it as an error, because it is not one.
 *   A **4409** (`ROOM_CLOSING_CODE`), a room being deleted, is not
 *   that: it is closed like any dropped socket and reconnected with backoff,
 *   and nothing is erased, because a close that crashed half-way leaves the
 *   room, and this member's queued edits for it, still there.
 * - **A presence roster** goes to `onPresence` and nowhere else. It is not a
 *   document change, so it never reaches the store; it is not an answer to
 *   anything this client sent, so it settles no deadline beyond what *any*
 *   frame settles (see "Nothing waits forever").
 * - **`sendPresence`** is the one outbound frame that is owed no answer, so it
 *   goes to the socket directly rather than through `#send`, which would arm
 *   the reply deadline over a frame the room may have nothing to say back to.
 *   It is throttled, dropped rather than queued while the socket is down, and
 *   the last position is sent again when a socket opens.
 *
 * And one rejection is not news: `recipe-shared` means the workspace passed a
 * command on to the room that now holds the recipe. It is settled like any
 * rejection, and reported to `onRecipeShared` rather than `onDropped`, because
 * the edit did land — just somewhere else.
 */

import type { Command, WorkspaceState } from '@beerwright/commands';
import { emptyWorkspace, makeCommand } from '@beerwright/commands';
import { newId } from '@/lib/ids';
import { ROOM_CLOSED_CODE } from '@/shared-recipes/protocol';
import type { WorkspaceStore } from '@/store/workspace-store';
import type { ConfirmedStore } from './confirmed-store';
import type { Outbox, OutboxKey } from './outbox';
import type { AckMessage, PresenceAt, PresencePeer, ServerMessage } from './protocol';
import {
  decodeServerMessage,
  encodeMessage,
  MAX_DELTA_COMMANDS,
  MAX_PUSH_COMMANDS,
  PING_FRAME,
  PONG_FRAME,
  presenceMessage,
  pullMessage,
  pushMessage,
  SYNC_PATH,
  SYNC_PROTOCOL_VERSION,
} from './protocol';
import type { DroppedCommand } from './reconcile';
import { confirm, isEmptyWorkspace, replay, sameWorkspace } from './reconcile';

/** What the sidebar shows. Six states; `stale` is the one that asks the brewer to act. */
export type SyncStatus =
  /** Signed out. Local-only, exactly as M2 behaved. */
  | 'guest'
  /** A socket is being opened for the first time. */
  | 'connecting'
  /** Connected, with work outstanding: catching up, or pushing. */
  | 'syncing'
  /** Connected, caught up, outbox empty. */
  | 'synced'
  /** No socket; a reconnect is scheduled. Edits still work and still queue. */
  | 'offline'
  /**
   * This tab's bundle speaks an older protocol than the server now requires.
   * Nothing is lost — edits still apply, persist and queue — but nothing will
   * sync until the page is reloaded, and the client has stopped trying.
   */
  | 'stale';

/** The bit of `WebSocket` this client uses. Narrow, so tests can supply a fake. */
export interface SyncSocket {
  send: (data: string) => void;
  close: () => void;
  addEventListener: (
    type: 'open' | 'message' | 'close' | 'error',
    /** `code` is a `close` event's status, which is how a room says it is gone. */
    listener: (event: { data?: unknown; code?: number }) => void,
  ) => void;
}

/** Reconnect timing. */
export interface BackoffOptions {
  /** First retry delay. */
  readonly baseMs: number;
  /** Ceiling for the exponential growth. */
  readonly maxMs: number;
  /** Fraction of the delay to randomise by, e.g. `0.25` for ±25%. */
  readonly jitter: number;
}

/** Defaults: 500 ms doubling to 30 s, ±25%. */
export const DEFAULT_BACKOFF: BackoffOptions = { baseMs: 500, maxMs: 30_000, jitter: 0.25 };

/** How long dispatches are coalesced before a push. One keystroke run, one frame. */
export const DEFAULT_PUSH_DELAY_MS = 50;

/**
 * How long an outbound message may go unanswered before the socket is dead.
 *
 * Long enough for a catch-up snapshot to cross a slow cellular link, short
 * enough that "Syncing" stops being able to mean forever.
 */
export const REPLY_DEADLINE_MS = 15_000;

/** How often an otherwise quiet socket is asked to prove it is still there. */
export const HEARTBEAT_MS = 30_000;

/**
 * How long the client waits before trying a rejected outbox write again.
 *
 * Once, and a second later. The failures worth retrying are transient — a quota
 * the browser was about to reclaim, a database blocked by another tab that is
 * about to close — and a device that refuses the write twice a second apart is
 * not going to be talked round by a third attempt.
 */
export const OUTBOX_RETRY_MS = 1_000;

/**
 * The least time between two presence frames.
 *
 * A brewer tabbing down an ingredient table moves several rows a second, and
 * the room answers every move with the whole roster to everybody in it. A
 * quarter of a second still reads as live on the other screen.
 */
export const PRESENCE_THROTTLE_MS = 250;

/**
 * Where an unacked command stands with the disk.
 *
 * - `pending` — the append has not answered yet. The push waits here, because
 *   the answer is coming and sending a later command first would reorder the
 *   log.
 * - `durable` — written, and `key` is the row that prunes it.
 * - `unsaved` — the append rejected twice and this device will not be keeping
 *   it. The push steps over it rather than waiting, since nothing is coming.
 */
type Durability = 'pending' | 'durable' | 'unsaved';

/**
 * One unacked command, as the client tracks it in memory.
 *
 * The same thing an `OutboxEntry` is, minus the assumption that it has already
 * been written. `key` is `null` until the append resolves, and is patched in
 * place afterwards — in place, because the object's identity is how the
 * append's continuation finds the entry again (or discovers that it is gone).
 *
 * `durability` is tracked beside the key rather than read off it, because
 * "no key yet" and "no key ever" are opposite instructions to `#push` and a
 * `null` cannot say which it is. See the module header.
 */
interface PendingEntry {
  /** The outbox row, or `null` while the write is in flight — or has failed. */
  key: OutboxKey | null;
  durability: Durability;
  readonly command: Command;
}

/** Options for {@link SyncClient}. */
export interface SyncClientOptions {
  readonly store: WorkspaceStore;
  readonly outbox: Outbox;
  /**
   * Where the confirmed prefix is remembered between sessions.
   *
   * Omitted, the client behaves as it always did: every session starts at
   * version 0 and the first pull asks for the whole log. Supplied, a returning
   * client resumes where it left off and the catch-up is a delta.
   */
  readonly confirmedStore?: ConfirmedStore;
  /** WebSocket URL. Defaults to {@link syncUrl} against the current origin. */
  readonly url?: string;
  /** Socket constructor. Defaults to a real `WebSocket`. */
  readonly createSocket?: (url: string) => SyncSocket;
  readonly backoff?: BackoffOptions;
  readonly pushDelayMs?: number;
  /** Overrides {@link REPLY_DEADLINE_MS}. Tests set it small. */
  readonly replyDeadlineMs?: number;
  /** Overrides {@link HEARTBEAT_MS}. Tests set it small. */
  readonly heartbeatMs?: number;
  /** Randomness for jitter. Injected so tests are deterministic. */
  readonly random?: () => number;
  readonly now?: () => number;
  readonly nextCommandId?: () => string;
  /** The signed-out workspace, for the first-sign-in rule. */
  readonly guestWorkspace?: () => Promise<WorkspaceState | null>;
  /** Called when the server workspace won and guest data was left where it was. */
  readonly onGuestConflict?: (guest: WorkspaceState) => void;
  /** Called with commands the replay or the server refused for ordinary reasons. */
  readonly onDropped?: (dropped: readonly DroppedCommand[]) => void;
  /**
   * Called when the server refused a create because the account is at its cap.
   *
   * Separated from {@link onDropped} because it is not an error: the command was
   * valid, the workspace is fine, and the honest response is an upgrade
   * conversation rather than a red toast. The optimistic recipe is rolled back
   * through the ordinary reconciliation either way — see PLAN §9.
   */
  readonly onPlanLimit?: (dropped: readonly DroppedCommand[]) => void;
  /** Called when the session is gone; the UI should return to guest mode. */
  readonly onUnauthorized?: () => void;
  /**
   * Called once, the moment the client gives up because the server speaks a
   * newer protocol (see {@link checkProtocol}).
   *
   * Going stale is the one moment the client *knows* a newer build exists.
   * The shell's own update check (`lib/shell-update.ts`) polls every five
   * minutes, so without this a tab that went stale seconds after a deploy
   * would wear "Reload needed" for most of that; with it the shell can look
   * for the build now and take it at the next quiet moment.
   */
  readonly onStale?: () => void;
  /**
   * Ask the server which protocol version it speaks, or `null` when that
   * cannot be learned right now.
   *
   * A browser `WebSocket` reports a refused upgrade as a bare `close` with no
   * status, so the 400 the Worker answers a stale `?v=` with looks exactly like
   * a dropped network. The client therefore asks this after the **second**
   * consecutive failed connect — one drop is a network, two in a row is worth a
   * question — and a number other than {@link SYNC_PROTOCOL_VERSION} moves it
   * to `stale`. `null` (could not ask) keeps the ordinary backoff going.
   */
  readonly checkProtocol?: () => Promise<number | null>;
  readonly onError?: (error: unknown) => void;
  /**
   * Called when the reads in front of the socket failed and the client fell
   * back to its backoff — see {@link SyncClient.start}.
   *
   * Separate from {@link onError}, which is the socket's channel and is
   * expected to fire on any flaky network. This one says the device's storage
   * would not open, which is rare, recoverable only by retry, and worth
   * reporting. Falls back to `onError` when it is not supplied, so the failure
   * is never silent.
   */
  readonly onStartFailure?: (error: unknown) => void;
  /**
   * Called when this device's storage would not *keep* an edit — the outbox
   * append rejected, and the retry rejected too.
   *
   * Separate from {@link onStartFailure}, which says the storage would not
   * open, and from {@link onError}, which is the socket's noisy channel. This
   * one is the quiet, serious case: the command is on screen and on its way to
   * the server, but this device is no longer holding a copy, so a reload before
   * the ack would lose it. Falls back to `onError` when it is not supplied, so
   * the failure is never silent. Not a toast — there is nothing for the brewer
   * to do, and the edit is very probably about to sync.
   */
  readonly onDurabilityFailure?: (error: unknown) => void;
  /**
   * Called once, when a room closes the socket with {@link ROOM_CLOSED_CODE}.
   *
   * The client has already stopped for good by then, so the consumer's job is
   * only to let the room go (PLAN-COLLAB C5). Not `onError`: a member who was
   * removed is not a fault.
   */
  readonly onGone?: () => void;
  /**
   * Called with a room's whole roster, every time it changes.
   *
   * Raw: the roster includes this member, and leaving them out is the
   * consumer's call. Presence never touches the store.
   */
  readonly onPresence?: (peers: readonly PresencePeer[]) => void;
  /**
   * Called when the server settled commands as `recipe-shared`: passed on to
   * the room that holds the recipe now. Not {@link onDropped}, since nothing
   * failed and there is nothing to show.
   */
  readonly onRecipeShared?: () => void;
}

/** The sync WebSocket URL for an origin. */
export function syncUrl(origin: string = globalThis.location?.origin ?? ''): string {
  const url = new URL(SYNC_PATH, origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('v', String(SYNC_PROTOCOL_VERSION));
  return url.toString();
}

/** Wrap a real `WebSocket` in the narrow {@link SyncSocket} shape. */
export function browserSocket(url: string): SyncSocket {
  const socket = new WebSocket(url);
  return {
    send: (data) => socket.send(data),
    close: () => socket.close(),
    addEventListener: (type, listener) => socket.addEventListener(type, listener as EventListener),
  };
}

export class SyncClient {
  readonly #options: SyncClientOptions;
  readonly #store: WorkspaceStore;
  readonly #outbox: Outbox;
  readonly #confirmedStore: ConfirmedStore | undefined;
  readonly #statusListeners = new Set<() => void>();

  /** The workspace at {@link SyncClient.getVersion}. Never persisted. */
  #confirmed: WorkspaceState = emptyWorkspace();
  #version = 0;

  /**
   * The outbox, mirrored in memory so replay does not hit IndexedDB — plus the
   * commands that are on screen but whose write has not committed yet.
   */
  #pending: PendingEntry[] = [];
  /** Serialises outbox appends so in-memory order matches on-disk order. */
  #appendQueue: Promise<void> = Promise.resolve();

  #socket: SyncSocket | null = null;
  #open = false;
  #started = false;
  /**
   * True once the outbox and the kept prefix have actually been read.
   *
   * What tells a reconnect whether it is resuming a socket or still owes the
   * reads that come before one. Cleared by `stop()`, since the next `start()`
   * reads the disk again.
   */
  #storageRead = false;
  /** A `#boot` in flight, so nothing starts a second set of reads over it. */
  #booting = false;
  #caughtUp = false;
  /**
   * True once this session has completed one catch-up, whatever has happened to
   * the socket since.
   *
   * `#caughtUp` answers a different question — every `#connect` clears it — and
   * the two things that need a session-level answer would both be wrong to use
   * it: the guest-adoption step must run once rather than once per reconnect,
   * and {@link SyncClient.hasCaughtUp} tells the UI whether this workspace has
   * ever met the server, which a dropped socket does not undo.
   */
  #everCaughtUp = false;
  #inFlight: readonly Command[] | null = null;

  #attempt = 0;
  /** The protocol question in flight, so a streak of failures asks it once. */
  #protocolCheck: Promise<void> | null = null;
  /** True once the server has answered with a newer protocol. Cleared by `stop()`. */
  #stale = false;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #pushTimer: ReturnType<typeof setTimeout> | null = null;
  /** Running while something outbound is still owed an answer. See {@link SyncClient.reconnect}. */
  #deadlineTimer: ReturnType<typeof setTimeout> | null = null;
  /** The base the deadline starts at and returns to. Fixed for the client's life. */
  readonly #baseDeadlineMs: number;
  /** How long the *next* deadline waits: the base, doubled by each silent socket. */
  #deadlineMs: number;
  /** When the running deadline was armed, so a frame can be called prompt or late. */
  #deadlineArmedAt: number | null = null;
  #heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  /** Outbox appends waiting to be tried again, so `stop()` can let go of them. */
  readonly #appendRetries = new Map<ReturnType<typeof setTimeout>, () => void>();
  #unobserve: (() => void) | null = null;
  #unlistenToNetwork: (() => void) | null = null;
  #unlistenToVisibility: (() => void) | null = null;

  /**
   * True once a room has closed with {@link ROOM_CLOSED_CODE}. Never cleared:
   * a room that has said it will not answer this member is not asked again.
   */
  #gone = false;
  /** The last position `sendPresence` was given, restored when a socket opens. */
  #presenceAt: PresenceAt | undefined = undefined;
  /** Running for {@link PRESENCE_THROTTLE_MS} after each presence frame. */
  #presenceTimer: ReturnType<typeof setTimeout> | null = null;
  /** A move arrived inside the window, so the window's end sends the last one. */
  #presenceQueued = false;

  #status: SyncStatus = 'guest';
  /** The queue length the listeners were last told about. See `#setStatus`. */
  #notifiedPending = 0;
  /** Whether the listeners have been told this session caught up. See `#setStatus`. */
  #notifiedCaughtUp = false;

  constructor(options: SyncClientOptions) {
    this.#options = options;
    this.#store = options.store;
    this.#outbox = options.outbox;
    this.#confirmedStore = options.confirmedStore;
    this.#baseDeadlineMs = options.replyDeadlineMs ?? REPLY_DEADLINE_MS;
    this.#deadlineMs = this.#baseDeadlineMs;
  }

  // -------------------------------------------------------------------------
  // Public surface
  // -------------------------------------------------------------------------

  /** Current status, as one stable value for `useSyncExternalStore`. */
  getStatus = (): SyncStatus => this.#status;

  /** The server version this client has confirmed. */
  getVersion = (): number => this.#version;

  /**
   * Whether this session has finished a catch-up, which is what "the workspace
   * on screen has met the server" means.
   *
   * Read by the pages that must not call a record missing before then — a
   * hydration can legitimately open an empty workspace, and an id page that
   * answers inside that window says a genuine batch was deleted. Never reset by
   * a reconnect: the workspace stays caught up even while the socket is not.
   */
  hasCaughtUp = (): boolean => this.#everCaughtUp;

  /**
   * Whether the outbox and the kept prefix have been read off this device.
   *
   * True from the moment both reads land; false until then, and false again
   * after `stop()`. The UI reads it to tell two clients that both say `offline`
   * apart: one that has its disk and is merely waiting for a network, whose
   * workspace on screen is real, and one whose storage would not open at all,
   * whose workspace may be the empty one a lost hydration race opened. Only the
   * first is believable about what it does *not* hold.
   */
  hasReadStorage = (): boolean => this.#storageRead;

  /**
   * How many commands are waiting to be accepted, counted from the dispatch
   * that caused them rather than from the write that recorded them.
   */
  getPendingCount = (): number => this.#pending.length;

  /** Subscribe to status changes. */
  subscribe = (listener: () => void): (() => void) => {
    this.#statusListeners.add(listener);
    return () => {
      this.#statusListeners.delete(listener);
    };
  };

  /**
   * Attach to the store, then load the outbox and connect. Idempotent.
   *
   * Resolves whatever happens. The reads can *fail* rather than hang —
   * `OPEN_TIMEOUT_MS` bounds a stalled `indexedDB.open()`, which a backgrounded
   * Android tab produces — and a rejection escaping here left the client in
   * `connecting` with no socket and nothing scheduled, which is the one status
   * that promises something is coming. {@link SyncClient.start} therefore hands
   * the reads to `#boot`, which treats a failure as any other failed attempt:
   * `offline`, and the socket's own backoff, retrying the reads themselves
   * because a rejected open is dropped from `idb.ts`'s memo and the next one is
   * fresh.
   *
   * **The store is observed before the read is awaited**, which is the same
   * invariant the module header states for `#onLocalCommand`, applied to the
   * one window that used to escape it. The outbox read is IndexedDB and takes a
   * turn or more; a command dispatched inside it, back when the observer was
   * attached afterwards, reached neither `#pending` nor the outbox. It was
   * invisible, so the first catch-up `#recompute` painted its effect off the
   * screen — and unlike the dispatch/durability window there was no disk row to
   * bring it back at the next frame. Attaching first makes the window
   * indistinguishable from any other moment: the command joins `#pending`
   * synchronously and its append is queued the usual way.
   *
   * The two halves are then merged rather than assigned, because both can hold
   * the same command: `#outbox.append` from inside the window may commit before
   * the read that is still open observes it. Disk order comes first — those
   * commands were dispatched in an earlier session — and the durable copy wins
   * a tie, since it already carries the key that prunes it.
   */
  async start(): Promise<void> {
    if (this.#started || this.#gone) return;
    this.#started = true;
    this.#setStatus('connecting');

    // Cleared first, so whatever is in `#pending` after the await was
    // definitionally dispatched inside this window rather than left over from a
    // previous `start()`/`stop()` cycle. A leftover that reached disk comes back
    // with the read; one that did not was never durable to begin with.
    this.#pending = [];
    this.#unobserve = this.#store.observeCommands((command) => this.#onLocalCommand(command));

    await this.#boot();
  }

  /**
   * The reads, and the connect behind them. Run again by the backoff until they
   * succeed.
   *
   * The observer is deliberately *not* re-attached per attempt: it belongs to
   * the `start()`/`stop()` cycle, and a command dispatched during a failed
   * attempt is in `#pending` exactly as one dispatched during a successful
   * read's window is. The merge below handles both the same way, since it asks
   * what is on disk rather than what is in memory.
   */
  async #boot(): Promise<void> {
    if (this.#booting) return;
    this.#booting = true;
    try {
      const { entries, unreadable } = await this.#outbox.load();
      // `stop()` may have run while the read was open — it detaches the
      // observer, so anything dispatched from here on is deliberately not ours.
      // Connecting a client that has been stopped would resurrect it.
      if (!this.#started) return;

      // Copied into mutable entries: everything already on disk is durable by
      // definition, so these start out keyed and never change.
      const onDisk = entries.map(
        (entry): PendingEntry => ({
          key: entry.key,
          durability: 'durable',
          command: entry.command,
        }),
      );
      const durable = new Set(onDisk.map((entry) => entry.command.commandId));
      const dispatchedWhileLoading = this.#pending.filter(
        (entry) => !durable.has(entry.command.commandId),
      );
      this.#pending = [...onDisk, ...dispatchedWhileLoading];
      if (unreadable.length > 0) await this.#outbox.remove(unreadable);

      // Resume the server prefix, so the first pull asks for what has happened
      // since rather than for everything that ever has. Read *after* the outbox
      // for the reason the outbox is read at all: `#pending` has to be whole
      // before a delta can be replayed over it. A `null` here is not a failure —
      // it is a first visit, a cleared record, or one this build cannot read —
      // and it leaves the client exactly where it used to start.
      const resumed = await this.#confirmedStore?.load();
      if (!this.#started) return;
      if (resumed !== null && resumed !== undefined) {
        this.#confirmed = resumed.state;
        this.#version = resumed.version;
      }
    } catch (error) {
      if (!this.#started) return;
      (this.#options.onStartFailure ?? this.#options.onError)?.(error);
      // Down, and scheduled: the retry comes back here rather than to
      // `#connect`, because the reads are what failed and the socket in front of
      // an unread outbox would push nothing and recompute against nothing.
      this.#scheduleReconnect();
      return;
    } finally {
      this.#booting = false;
    }

    this.#storageRead = true;
    this.#listenToNetwork();
    this.#listenToVisibility();
    this.#connect();
  }

  /**
   * Take the next step towards being connected, whichever one is outstanding.
   *
   * Every path back from `offline` goes through here rather than straight to
   * `#connect`, because a client whose reads never succeeded has no outbox and
   * no prefix, and a socket opened over that would catch up against a workspace
   * it cannot vouch for.
   */
  #resume(): void {
    if (this.#storageRead) {
      this.#connect();
      return;
    }
    void this.#boot();
  }

  /**
   * Detach from the store, drop the socket and stop reconnecting.
   *
   * The mirror window — commands dispatched *after* this — deliberately gets no
   * equivalent treatment. A stopped client owns nothing: it recomputes nothing,
   * so it can paint nothing away, and the edits stay on screen and in M2's
   * optimistic snapshot exactly as a guest's do. They do not reach this
   * outbox, which is the honest outcome for an `unauthorized` socket — the
   * account that would have received them is the one the server just refused.
   * A later `start()` reads the disk and begins again from there.
   */
  stop(): void {
    this.#started = false;
    this.#storageRead = false;
    this.#confirmedStore?.dispose();
    this.#unobserve?.();
    this.#unobserve = null;
    this.#unlistenToNetwork?.();
    this.#unlistenToNetwork = null;
    this.#unlistenToVisibility?.();
    this.#unlistenToVisibility = null;
    this.#clearTimer('reconnect');
    this.#clearTimer('push');
    this.#releaseAppendRetries();
    this.#clearTimer('presence');
    this.#presenceQueued = false;
    this.#presenceAt = undefined;
    this.#stale = false;
    this.#deadlineMs = this.#baseDeadlineMs;
    this.#deadlineArmedAt = null;
    this.#dropSocket();
    this.#setStatus('guest');
  }

  /** Push now rather than waiting out the coalescing delay. For tests and `beforeunload`. */
  flush(): void {
    this.#clearTimer('push');
    this.#push();
    // The prefix too: a tab being hidden is the moment its position is most
    // worth keeping, and the debounce may not have fired.
    void this.#confirmedStore?.flush();
  }

  /**
   * Throw this socket away and open another one, now.
   *
   * The tap behind the sidebar indicator, and the thing the reply deadline does
   * when it fires. Both are the same move for the same reason: the socket we
   * have is not carrying anything, and the reconnect path already replays the
   * outbox from the far side, so reaching it is the whole recovery.
   *
   * `#attempt` is reset because the backoff guards against a server that is
   * refusing connections, which is not what a silent open socket indicates. If
   * the server *is* down, this connect fails and the ordinary backoff takes over
   * from there. Called while `offline` it skips the wait; called while
   * `connecting` it starts the handshake over; called twice it is the second
   * call that stands. Called while `stale` it does nothing, because `#connect`
   * refuses there on purpose and dropping the socket would only make the tab
   * quieter.
   */
  reconnect(): void {
    if (!this.#started || this.#stale) return;
    this.#dropSocket();
    this.#attempt = 0;
    this.#resume();
  }

  /**
   * Tell a room where this member is. Rooms only: the workspace object answers
   * a presence frame with `bad-message`.
   *
   * At most one frame per {@link PRESENCE_THROTTLE_MS}: the first move goes at
   * once, and the moves inside the window collapse into the last of them, sent
   * as the window closes. A socket that is not open drops the frame rather than
   * queuing it — a position is only worth sending while it is current — but
   * the position is remembered, and a socket that opens sends it once, so a
   * reconnect puts the member back where they were.
   */
  sendPresence(at: PresenceAt): void {
    this.#presenceAt = at;
    if (!this.#open || this.#socket === null) return;
    if (this.#presenceTimer !== null) {
      this.#presenceQueued = true;
      return;
    }
    this.#writePresence(at);
  }

  /** A socket just opened: put the member back where they were, once. */
  #restorePresence(): void {
    const at = this.#presenceAt;
    if (at === undefined) return;
    this.#clearTimer('presence');
    this.#presenceQueued = false;
    this.#writePresence(at);
  }

  /**
   * Send one presence frame and open the throttle window behind it.
   *
   * Straight to the socket, not through `#send`: the room may answer a move
   * with nothing, and `#send` arms the reply deadline, which would then call a
   * healthy socket dead.
   */
  #writePresence(at: PresenceAt): void {
    if (this.#socket === null) return;
    try {
      this.#socket.send(encodeMessage(presenceMessage(at)));
    } catch (error) {
      this.#options.onError?.(error);
    }
    this.#presenceTimer = setTimeout(() => {
      this.#presenceTimer = null;
      if (!this.#presenceQueued) return;
      this.#presenceQueued = false;
      const latest = this.#presenceAt;
      if (!this.#open || this.#socket === null || latest === undefined) return;
      this.#writePresence(latest);
    }, PRESENCE_THROTTLE_MS);
  }

  /**
   * The room will not answer this member again. Stop, for good, and say so.
   *
   * `stop()` is the whole of the teardown; `#gone` is what makes it permanent,
   * since `start()` refuses once it is set. The socket is let go of first
   * because it has already closed.
   */
  #onGone(): void {
    this.#gone = true;
    this.#socket = null;
    this.stop();
    this.#options.onGone?.();
  }

  // -------------------------------------------------------------------------
  // Network events
  // -------------------------------------------------------------------------

  /**
   * React to the browser's own online/offline events.
   *
   * Without this, losing Wi-Fi leaves a socket that is open as far as the page
   * is concerned until a TCP timeout notices — potentially minutes of the UI
   * claiming "synced" while nothing is being delivered. Closing on `offline`
   * makes the indicator honest immediately; reconnecting on `online` skips the
   * backoff, because the one thing the backoff is protecting against (a server
   * that is down) is not what just happened.
   *
   * `online` on a socket the client still believes in is the interesting case,
   * and it used to return early. A phone that moves from Wi-Fi to cellular
   * fires `online` without ever firing `close`, so the event that is the best
   * evidence there is that the old connection is gone was the one event this
   * client ignored — leaving a `synced` badge over a dead wire until the
   * brewer's next edit armed a deadline. So it does what the visibility
   * handler does and asks: a ping, which arms the deadline, and a flush if
   * anything is queued. The answer settles it, or the deadline does.
   */
  #listenToNetwork(): void {
    const target = globalThis as Partial<Pick<Window, 'addEventListener' | 'removeEventListener'>>;
    if (typeof target.addEventListener !== 'function') return;

    const onOffline = () => {
      if (this.#socket === null) return;
      this.#dropSocket();
      this.#scheduleReconnect();
    };
    const onOnline = () => {
      if (!this.#started || this.#stale) return;
      if (this.#open && this.#socket !== null) {
        this.#ping();
        if (this.#pending.length > 0) this.flush();
        return;
      }
      this.#attempt = 0;
      this.#connect();
    };

    target.addEventListener('offline', onOffline);
    target.addEventListener('online', onOnline);
    this.#unlistenToNetwork = () => {
      target.removeEventListener?.('offline', onOffline);
      target.removeEventListener?.('online', onOnline);
    };
  }

  /**
   * React to the tab coming back.
   *
   * The case this is for is a phone: the app is backgrounded, the connection
   * moves from Wi-Fi to cellular or simply dies, and the browser fires neither
   * `close` nor `offline` — a network *switch* is not an outage as far as the
   * page is concerned. The socket is half-open and looks perfectly healthy, and
   * the client has no reason of its own to doubt it.
   *
   * So on the way back in the client asks. A ping either comes back inside the
   * reply deadline, which settles the question, or it does not and the deadline
   * reconnects. If we are already down with a retry pending, the tab returning
   * is better news about the network than the backoff is, so it connects now —
   * the same shortcut the `online` handler takes.
   *
   * `globalThis.document` is read defensively, exactly as the network listener
   * reads `globalThis.addEventListener`: part of this module's own suite runs in
   * node, where there is no document, and a client that has no tab to watch
   * should simply not watch one rather than fail to start.
   */
  #listenToVisibility(): void {
    const target = globalThis.document as Document | undefined;
    if (target === undefined || typeof target.addEventListener !== 'function') return;

    const onVisible = () => {
      if (!this.#started || this.#stale) return;
      if (target.visibilityState !== 'visible') return;
      if (this.#open && this.#socket !== null) {
        this.#ping();
        if (this.#pending.length > 0) this.flush();
        return;
      }
      if (this.#reconnectTimer !== null) {
        this.#attempt = 0;
        this.#connect();
      }
    };

    target.addEventListener('visibilitychange', onVisible);
    this.#unlistenToVisibility = () => {
      target.removeEventListener?.('visibilitychange', onVisible);
    };
  }

  /** Whether the tab is in front of the brewer. True where there is no document to ask. */
  #isVisible(): boolean {
    const target = globalThis.document as Document | undefined;
    return target === undefined || target.visibilityState === 'visible';
  }

  // -------------------------------------------------------------------------
  // Liveness
  // -------------------------------------------------------------------------

  /**
   * Send the heartbeat.
   *
   * Not a protocol message: it goes out as the literal {@link PING_FRAME}, and
   * workerd answers it from the runtime without waking the hibernating object
   * (`workspace-do.ts`, `setWebSocketAutoResponse`). That is what makes beating
   * once every {@link HEARTBEAT_MS} affordable per client rather than a wake per
   * client per interval.
   *
   * The deadline is armed *before* the send, because a socket may answer
   * synchronously and clearing a deadline that has not been armed yet would
   * leave it running with nothing outstanding.
   */
  #ping(): void {
    if (this.#socket === null) return;
    this.#armDeadline();
    try {
      this.#socket.send(PING_FRAME);
    } catch (error) {
      this.#options.onError?.(error);
    }
  }

  /**
   * Beat while the socket is open, the tab is in front, and nothing else is
   * already owed an answer — an outstanding pull or push carries its own
   * deadline, and a second probe would tell us nothing the first will not.
   *
   * A hidden tab deliberately does not beat: that is what the visibility ping
   * is for, and iOS throttles the timer to uselessness anyway.
   */
  #startHeartbeat(): void {
    this.#clearTimer('heartbeat');
    this.#heartbeatTimer = setInterval(() => {
      if (!this.#open || this.#socket === null) return;
      if (this.#deadlineTimer !== null) return;
      if (!this.#isVisible()) return;
      this.#ping();
    }, this.#options.heartbeatMs ?? HEARTBEAT_MS);
  }

  /**
   * Start the clock on an answer, unless one is already running.
   *
   * One timer for the whole socket rather than one per request: any frame at
   * all proves the transport and the object are alive, so any frame clears it,
   * and `#onFrame` re-arms it if the client is still waiting on something. That
   * is what stops another device's broadcast from standing in for the ack a
   * push never got.
   *
   * `#deadlineMs` rather than the base, because how long this client is
   * currently prepared to wait is a property of the network it has been talking
   * to — see `#onDeadline` and the module header.
   */
  #armDeadline(): void {
    if (this.#deadlineTimer !== null) return;
    this.#deadlineArmedAt = this.#now();
    this.#deadlineTimer = setTimeout(() => {
      this.#deadlineTimer = null;
      this.#deadlineArmedAt = null;
      this.#onDeadline();
    }, this.#deadlineMs);
  }

  /** The clock, injected so a test's fake timers are the only time there is. */
  #now(): number {
    return (this.#options.now ?? Date.now)();
  }

  /**
   * Nothing came back. Treat the socket as dead, wait longer next time, and
   * open another.
   *
   * The doubling is what makes a slow link recoverable rather than terminal. A
   * round trip longer than the deadline means every socket is thrown away a
   * moment before its answer arrives, and since the reconnect starts the same
   * question over, the client would sit at version zero for as long as the
   * network was bad — pinned, once, by `chaos/bad-network.test.ts`. Doubling up
   * to eight times the base eventually covers the round trip, and the first
   * frame across resets it, so a link that recovers costs nothing afterwards.
   *
   * Only a socket that **opened** earns the doubling. A socket that never
   * opened is not a slow answer, it is a connect that failed, and the backoff
   * is already the answer to that — in practice it cannot reach here at all,
   * because nothing is sent before `open` and therefore no deadline is armed,
   * but the condition says what the rule is rather than relying on that.
   *
   * What the client has learned here is kept until a *prompt* reply disproves
   * it; `#onFrame` is the other half of the rule.
   */
  #onDeadline(): void {
    if (!this.#started || this.#socket === null) return;
    if (this.#open) this.#deadlineMs = Math.min(this.#deadlineMs * 2, this.#baseDeadlineMs * 8);
    this.#options.onError?.(new Error('The sync socket stopped answering; reconnecting.'));
    this.reconnect();
  }

  /**
   * Drop the current socket, leaving nothing scheduled.
   *
   * The socket is nulled *before* it is closed, because the close handler is
   * what schedules a reconnect and it ignores sockets that are no longer
   * current — so callers decide what happens next rather than racing it.
   */
  #dropSocket(): void {
    const socket = this.#socket;
    this.#socket = null;
    this.#open = false;
    this.#inFlight = null;
    this.#clearTimer('deadline');
    this.#clearTimer('heartbeat');
    try {
      socket?.close();
    } catch (error) {
      this.#options.onError?.(error);
    }
  }

  // -------------------------------------------------------------------------
  // Local writes
  // -------------------------------------------------------------------------

  /**
   * Take ownership of a command the store has just applied optimistically.
   *
   * The `#pending.push` happens **now**, in the same synchronous turn as the
   * dispatch, and that placement is the whole point: from here until the server
   * settles it, the command is part of every `replay(confirmed, #pending)` the
   * client computes, so no frame arriving mid-write can paint it away. The
   * module header has the failure it prevents.
   */
  #onLocalCommand(command: Command): void {
    const entry: PendingEntry = { key: null, durability: 'pending', command };
    this.#pending.push(entry);

    // Chained rather than awaited: `observeCommands` is synchronous, and two
    // dispatches in one tick must reach the outbox in dispatch order. The
    // retry waits inside the chain for the same reason — a command that goes
    // to disk while an earlier one is still trying would be out of order on it.
    this.#appendQueue = this.#appendQueue.then(() => this.#append(entry));

    this.#refreshStatus();
  }

  /**
   * Write one command to the outbox, and try again once if the disk says no.
   *
   * The first rejection is deliberately not reported. A write the retry repairs
   * is not news, and the channel this would shout down is the one that says the
   * device has actually stopped keeping the brewer's edits. The second
   * rejection is that news, and it goes out once.
   */
  async #append(entry: PendingEntry): Promise<void> {
    try {
      this.#onDurable(entry, (await this.#outbox.append(entry.command)).key);
      return;
    } catch {
      // Tried again below, a second from now.
    }

    await this.#waitToRetryAppend();
    // The world moved while we waited: the client was stopped, or an ack or a
    // replay settled the command and `#pending` no longer holds this entry.
    // Either way there is nothing left to write.
    if (!this.#started || !this.#pending.includes(entry)) return;

    try {
      this.#onDurable(entry, (await this.#outbox.append(entry.command)).key);
    } catch (error: unknown) {
      this.#onUnsaved(entry, error);
    }
  }

  /** A second, as a promise `stop()` can resolve early rather than leave hanging. */
  #waitToRetryAppend(): Promise<void> {
    return new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        this.#appendRetries.delete(timer);
        resolve();
      }, OUTBOX_RETRY_MS);
      this.#appendRetries.set(timer, resolve);
    });
  }

  /** Let go of every waiting retry, and let its continuation run and find us stopped. */
  #releaseAppendRetries(): void {
    for (const [timer, resolve] of this.#appendRetries) {
      clearTimeout(timer);
      resolve();
    }
    this.#appendRetries.clear();
  }

  /**
   * The disk will not keep this command. Say so, and send it anyway.
   *
   * Marking the entry rather than leaving it looking like a write in flight is
   * the whole fix: `#push` waits on a write that has not answered and steps
   * over one that never will, so the queue moves and the order the brewer typed
   * is kept. See the module header for the trade this makes and what is left of
   * it.
   */
  #onUnsaved(entry: PendingEntry, error: unknown): void {
    entry.durability = 'unsaved';
    (this.#options.onDurabilityFailure ?? this.#options.onError)?.(error);
    this.#schedulePush();
    this.#refreshStatus();
  }

  /**
   * The command reached disk. Give its entry the key that can later prune it.
   *
   * Unless the entry is gone — which is normal, not exceptional. Between the
   * dispatch and this continuation the command may have been settled by an ack
   * or dropped by a replay that found it no longer applicable, and either way
   * `#pending` has moved on without it. The row it just wrote is then an orphan
   * that no settlement path holds a key for, so nothing would ever delete it and
   * the next reload would replay a command the server has already answered.
   * Deleting it here is the only moment that knowledge exists in one place.
   *
   * Identity, not `commandId`, decides: the entry object is the token the append
   * was started with, so `includes` answers "is *this* dispatch still live?"
   * without assuming ids are unique across the queue's whole history.
   */
  #onDurable(entry: PendingEntry, key: OutboxKey): void {
    if (!this.#pending.includes(entry)) {
      this.#prune([key]);
      return;
    }
    entry.key = key;
    entry.durability = 'durable';
    this.#schedulePush();
    this.#refreshStatus();
  }

  /** Delete outbox rows, fire-and-forget; a failure to prune is not a data loss. */
  #prune(keys: readonly OutboxKey[]): void {
    if (keys.length === 0) return;
    void this.#outbox.remove(keys).catch((error: unknown) => {
      this.#options.onError?.(error);
    });
  }

  /**
   * The keys of entries that have left `#pending`.
   *
   * A `null` here is a command settled before its write committed, or one whose
   * write never will. It is dropped rather than deleted, because there is
   * nothing to delete — either nothing yet, in which case `#onDurable` finds the
   * entry missing and removes the row the moment it exists, or nothing at all,
   * because the append rejected and no row was written.
   */
  #keysOf(entries: readonly PendingEntry[]): readonly OutboxKey[] {
    return entries.flatMap((entry) => (entry.key === null ? [] : [entry.key]));
  }

  #schedulePush(): void {
    if (this.#pushTimer !== null) return;
    this.#pushTimer = setTimeout(() => {
      this.#pushTimer = null;
      this.#push();
    }, this.#options.pushDelayMs ?? DEFAULT_PUSH_DELAY_MS);
  }

  /**
   * Send the longest prefix of the queue whose writes have **answered**.
   *
   * It stops at the first entry whose append is still in flight, and does not
   * step over it to reach a later one that has committed. Skipping would push
   * commands in an order the user never typed and the outbox does not record —
   * and the server's log order is the truth everything else is reconciled
   * against, so a reorder is not a cosmetic bug.
   *
   * Waiting on the head keeps the crash semantics the outbox was built for: the
   * wait is bounded by one IndexedDB write, and `#onDurable` schedules the push
   * that picks the queue back up. An **unsaved** entry is the case where that
   * bound does not exist, so it is sent in place rather than waited on — see
   * the module header. It is the one command in flight that a power cut would
   * lose, and it is in flight precisely because that is the better of the two
   * outcomes available to it.
   */
  #push(): void {
    if (!this.#open || this.#socket === null) return;
    if (!this.#caughtUp) return;
    if (this.#inFlight !== null) return;
    if (this.#pending.length === 0) return;

    const batch: Command[] = [];
    for (const entry of this.#pending) {
      if (entry.durability === 'pending') break;
      batch.push(entry.command);
      if (batch.length === MAX_PUSH_COMMANDS) break;
    }
    if (batch.length === 0) return;

    this.#inFlight = batch;
    this.#send(pushMessage(batch, this.#version));
    this.#refreshStatus();
  }

  // -------------------------------------------------------------------------
  // Socket lifecycle
  // -------------------------------------------------------------------------

  #connect(): void {
    if (!this.#started || this.#stale) return;
    this.#clearTimer('reconnect');
    this.#clearTimer('deadline');
    this.#clearTimer('heartbeat');
    this.#open = false;
    this.#caughtUp = false;
    this.#inFlight = null;
    this.#setStatus('connecting');

    const create = this.#options.createSocket ?? browserSocket;
    let socket: SyncSocket;
    try {
      socket = create(this.#options.url ?? syncUrl());
    } catch (error) {
      this.#options.onError?.(error);
      this.#scheduleReconnect();
      return;
    }
    this.#socket = socket;

    socket.addEventListener('open', () => {
      if (this.#socket !== socket) return;
      this.#open = true;
      this.#attempt = 0;
      this.#startHeartbeat();
      this.#send(pullMessage(this.#version));
      this.#restorePresence();
      this.#refreshStatus();
    });
    socket.addEventListener('message', (event) => {
      if (this.#socket !== socket) return;
      this.#onFrame(event.data);
    });
    socket.addEventListener('close', (event) => {
      if (this.#socket !== socket) return;
      if (event.code === ROOM_CLOSED_CODE) {
        this.#onGone();
        return;
      }
      // Every other code, `ROOM_CLOSING_CODE` included, is a dropped socket.
      this.#socket = null;
      this.#open = false;
      this.#inFlight = null;
      this.#clearTimer('deadline');
      this.#clearTimer('heartbeat');
      this.#scheduleReconnect();
    });
    socket.addEventListener('error', () => {
      if (this.#socket !== socket) return;
      // `close` follows an `error` in every implementation; reconnecting here
      // as well would double-schedule.
      this.#options.onError?.(new Error('The sync socket errored.'));
    });
  }

  #scheduleReconnect(): void {
    if (!this.#started || this.#stale) return;
    this.#setStatus('offline');
    // The second failure in a row is when a refused upgrade stops looking like
    // a blip (`#attempt` counts failures already scheduled, so it reads 1 on the
    // second). Asked once per streak; a check already in flight is not repeated.
    const check = this.#options.checkProtocol;
    if (this.#attempt >= 1 && this.#protocolCheck === null && check !== undefined) {
      this.#protocolCheck = check()
        .then((server) => {
          if (server !== null && server !== SYNC_PROTOCOL_VERSION) this.#markStale();
        })
        .catch((error: unknown) => this.#options.onError?.(error))
        .finally(() => {
          this.#protocolCheck = null;
        });
    }
    const { baseMs, maxMs, jitter } = this.#options.backoff ?? DEFAULT_BACKOFF;
    const random = this.#options.random ?? Math.random;
    const exponential = Math.min(baseMs * 2 ** this.#attempt, maxMs);
    const spread = exponential * jitter;
    const delay = Math.max(0, Math.round(exponential - spread + random() * spread * 2));
    this.#attempt += 1;
    this.#clearTimer('reconnect');
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null;
      this.#resume();
    }, delay);
  }

  /**
   * Stop trying: the server has moved on and only a reload can follow it.
   *
   * Everything `stop()` would tear down is left standing. The store observer
   * keeps mirroring dispatches into the outbox, so nothing typed in a stale tab
   * is lost — the next load reads that outbox and pushes it, on the protocol
   * the new bundle speaks. Only the reconnect timer goes, because every further
   * attempt would be the same 400. The deploy's own quiet-moment reload
   * (`lib/shell-update.ts`) usually gets there first; this is for the tab it
   * has not reached yet, and for the brewer who wants to know why the badge
   * changed.
   *
   * An open socket outranks the answer. `checkProtocol` is asked while the
   * client is down and can still be in flight when a later connect succeeds, and
   * a socket that is open is *proof* the versions agree: the Worker refuses a
   * mismatched `?v=` at the upgrade, so nothing gets this far on the wrong
   * protocol. Marking that connection stale would stop nothing — the socket is
   * never closed here — and would put a "reload me" badge over pushes that are
   * flowing. The answer is simply out of date; the next streak asks again.
   */
  #markStale(): void {
    if (!this.#started || this.#open) return;
    this.#clearTimer('reconnect');
    this.#stale = true;
    this.#setStatus('stale');
    this.#options.onStale?.();
  }

  /**
   * Send a protocol message and start the clock on its answer.
   *
   * Armed before the send rather than after it, because a socket can answer
   * synchronously — the test server does, and so does a same-tick error — and a
   * deadline armed after the reply that would have cleared it is a deadline
   * nothing will clear.
   */
  #send(message: Parameters<typeof encodeMessage>[0]): void {
    if (this.#socket === null) return;
    this.#armDeadline();
    try {
      this.#socket.send(encodeMessage(message));
    } catch (error) {
      this.#options.onError?.(error);
    }
  }

  // -------------------------------------------------------------------------
  // Server frames
  // -------------------------------------------------------------------------

  /**
   * A frame arrived, whatever it says.
   *
   * The deadline is cleared first and unconditionally: *any* frame is proof
   * that the transport and the object are alive, which is the only question the
   * deadline asks. It is then re-armed if the client is still waiting on
   * something, so a broadcast from another device cannot stand in for the ack a
   * push never received.
   *
   * {@link PONG_FRAME} is handled before the decoder, because it is not a
   * message and running it through `decodeServerMessage` would report the
   * heartbeat as an unreadable frame.
   */
  #onFrame(raw: unknown): void {
    // A frame that came back quickly is the link saying it no longer needs the
    // patience the silence bought, so the next wait is the base one again. A
    // frame that took longer than the base deadline is the opposite: it only
    // arrived at all because of the doubling, and giving that back now would
    // leave whatever is sent next with a bound this link has just been measured
    // to miss. So the current value stands until something is answered
    // promptly. A frame with nothing outstanding — another device's broadcast
    // while this one is idle — has no wait to be quick relative to, and says
    // nothing either way.
    const armedAt = this.#deadlineArmedAt;
    if (armedAt !== null && this.#now() - armedAt < this.#baseDeadlineMs) {
      this.#deadlineMs = this.#baseDeadlineMs;
    }
    this.#clearTimer('deadline');
    if (raw !== PONG_FRAME) this.#handleMessage(raw);
    if (this.#started && this.#open && (!this.#caughtUp || this.#inFlight !== null)) {
      this.#armDeadline();
    }
  }

  #handleMessage(raw: unknown): void {
    const decoded = decodeServerMessage(raw);
    if (!decoded.ok) {
      this.#options.onError?.(new Error(`Unreadable sync frame: ${decoded.reason}`));
      return;
    }
    const message: ServerMessage = decoded.message;

    switch (message.type) {
      case 'snapshot': {
        this.#confirmed = message.state;
        this.#version = message.version;
        this.#finishCatchUp(true);
        return;
      }
      case 'commands': {
        // Already applied — a broadcast that raced a pull.
        if (message.toVersion <= this.#version) {
          this.#finishCatchUp(false);
          return;
        }
        if (message.fromVersion !== this.#version) {
          // A gap: the frame starts after where we are. Ask for the missing
          // span rather than applying commands out of order.
          this.#send(pullMessage(this.#version));
          return;
        }
        // Before the frame is folded in, because afterwards the local copies of
        // the commands it carries are refusals waiting to happen.
        this.#settleRedelivered(message.commands);
        const advanced = confirm(this.#confirmed, message.commands);
        if (!advanced.ok) {
          this.#resync(advanced.error);
          return;
        }
        this.#confirmed = advanced.state;
        this.#version = message.toVersion;
        // A **full** page is not the end of the log. The server slices at
        // `MAX_DELTA_COMMANDS` (`workspace-do.ts`'s `#slice`), and the frame
        // carries no "more to come" flag, so a page of exactly that size is
        // the only signal there is that the log continues past it.
        //
        // Marking a catch-up finished here was a real bug. A workspace with
        // 983 commands arrives in two frames, and the first leaves the client
        // holding the workspace *as it was at version 500* — months of edits
        // short — while `#refreshStatus` announced `synced`. Anything that
        // waits for a settled workspace then ran against a half-replayed one.
        // `useIngredientLinkBackfill` did: it planned links against the old
        // state, wrote one the workspace already had, and latched, so it did
        // that once per load forever, churning a recipe's `updatedAt` and its
        // version history. The brewer saw a recipe that said "just now" on
        // every refresh of something they had not touched in a week.
        //
        // So: pull the next page and stay `syncing`. Nothing is recomputed
        // either, which means a half-replayed workspace is never painted.
        if (message.commands.length === MAX_DELTA_COMMANDS) {
          this.#send(pullMessage(this.#version));
          return;
        }
        // A caught-up client is receiving a live broadcast, and its commands are
        // the whole difference from the screen; a catch-up's last page is not.
        // Our own redelivered commands can land in `over` too, which only ever
        // costs an undo entry that meets them and never keeps a stale one.
        this.#finishCatchUp(
          message.commands.length > 0,
          this.#caughtUp ? message.commands : undefined,
        );
        return;
      }
      case 'ack': {
        this.#onAck(message);
        return;
      }
      case 'presence': {
        this.#options.onPresence?.(message.peers);
        return;
      }
      case 'error': {
        if (message.code === 'unauthorized') {
          this.#options.onUnauthorized?.();
          this.stop();
          return;
        }
        this.#options.onError?.(new Error(`${message.code}: ${message.message}`));
        if (message.code === 'bad-message' || message.code === 'internal') {
          // Both mean the same thing to the queue: the batch in flight will
          // never be acked, so holding `#inFlight` would block every later push
          // for the life of this socket. Released, and deliberately *not*
          // re-sent — an exception inside the object is almost certainly
          // deterministic on that batch, and retrying it on an open socket
          // would be a hot loop. The commands stay in `#pending` and go out
          // with the next scheduled push or the next reconnect.
          this.#inFlight = null;
          this.#refreshStatus();
        }
        return;
      }
    }
  }

  /**
   * Settle the commands of ours that the server has just handed back.
   *
   * An ack can lose its race with a catch-up. The push crosses, the object
   * applies it, and the answer goes into a socket that is already gone — a
   * half-open connection the deadline is about to throw away, a Wi-Fi switch,
   * a tab that came back to a dead wire. The reconnect then pulls from a
   * version the command is already part of, so it arrives back inside the
   * server's own log while the local copy is still in `#pending`.
   *
   * Folding that frame in and then replaying the local copy over it is the
   * wrong end of the telescope: the reducer sees a create of a recipe that
   * already exists, refuses it, and `#recompute` reports a drop — so the app
   * toasts "An edit could not be synced" about the one edit we have just been
   * told did sync. The screen is right either way; only the story is wrong.
   *
   * So a command in this frame is *settled*, and settled silently. The
   * server's log is the truth about what landed, and this command is in it;
   * there is nothing for the brewer to do and nothing to say. The row goes
   * with it — a `null` key is a write still in flight, which `#keysOf` skips
   * and `#onDurable` cleans up when it lands and finds its entry gone.
   */
  #settleRedelivered(commands: readonly Command[]): void {
    if (commands.length === 0 || this.#pending.length === 0) return;
    const ids = new Set(commands.map((command) => command.commandId));
    const settled = this.#pending.filter((entry) => ids.has(entry.command.commandId));
    if (settled.length === 0) return;
    this.#pending = this.#pending.filter((entry) => !ids.has(entry.command.commandId));
    this.#prune(this.#keysOf(settled));
    this.#refreshStatus();
  }

  #onAck(ack: AckMessage): void {
    const batch = this.#inFlight ?? [];
    this.#inFlight = null;

    if (ack.fromVersion !== this.#version) {
      // The server applied our batch on top of a prefix we have not seen. It
      // sends the gap before the ack, so this means a frame was lost; start over.
      this.#resync(null);
      return;
    }

    const byId = new Map(batch.map((command) => [command.commandId, command]));
    // Whatever is still queued *now*. A command can have left the outbox since
    // the push — a snapshot that arrived first may already have made it
    // unapplicable, in which case the replay reported it and the server's
    // rejection is the same news arriving twice.
    const stillPending = new Set(this.#pending.map((entry) => entry.command.commandId));
    const accepted: { version: number; command: Command }[] = [];
    const terminal = new Set<string>();
    const rejected: DroppedCommand[] = [];
    let redelivered = false;

    for (const result of ack.results) {
      terminal.add(result.commandId);
      const command = byId.get(result.commandId);
      if (result.status === 'applied' && command !== undefined && result.version !== null) {
        accepted.push({ version: result.version, command });
      } else if (
        result.status === 'rejected' &&
        command !== undefined &&
        result.error !== null &&
        stillPending.has(result.commandId)
      ) {
        rejected.push({ command, error: result.error });
      }
      // `duplicate` means the command is already inside `confirmed`, at a
      // version at or below `fromVersion`. If it was still queued, the prefix
      // that holds it can only have come in a snapshot (a `commands` frame
      // would have settled it, `#settleRedelivered`), and every replay since
      // has applied it a second time on top of whatever came after it. So the
      // screen is recomputed once it is out of the queue, below: settling it
      // quietly left our old write painted over a newer one of somebody
      // else's, with the client saying `synced`.
      else if (result.status === 'duplicate' && stillPending.has(result.commandId)) {
        redelivered = true;
      }
    }

    // The ack has to add up. `#version` is set from the frame below, but
    // `#confirmed` only ever gains the results this client could actually match
    // and fold in — an `applied` result naming a command outside the batch, or
    // carrying no version, silently widens the gap between the two. Once
    // `#version` over-states `#confirmed`, every later `commands` frame passes
    // the contiguity test in `#onFrame` and applies onto a base that is missing
    // commands, so the corruption is permanent and its eventual repaint looks
    // like ordinary remote state. Only applied results advance the server's
    // version — duplicates and rejections do not — so the arithmetic is exact.
    //
    // Today's Durable Object cannot produce such a frame. This is a guard
    // against the framing changing, or something between the two rewriting it,
    // and it costs one addition per ack.
    if (ack.version !== ack.fromVersion + accepted.length) {
      this.#resync(
        new Error(
          `Ack arithmetic does not add up: ${ack.fromVersion} + ${accepted.length} accepted ` +
            `!== ${ack.version}. Resyncing rather than trusting the version.`,
        ),
      );
      return;
    }

    accepted.sort((a, b) => a.version - b.version);
    const advanced = confirm(
      this.#confirmed,
      accepted.map((entry) => entry.command),
    );
    if (!advanced.ok) {
      this.#resync(advanced.error);
      return;
    }
    this.#confirmed = advanced.state;
    this.#version = ack.version;
    this.#rememberPrefix();

    const settled = this.#pending.filter((entry) => terminal.has(entry.command.commandId));
    this.#pending = this.#pending.filter((entry) => !terminal.has(entry.command.commandId));
    this.#prune(this.#keysOf(settled));

    if (rejected.length > 0) this.#report(rejected);
    // The optimistic state still contains the rejected command's effect, or a
    // redelivered one's second application. A second application often
    // changes nothing (nobody wrote the field after us), and then the screen
    // is already right and its undo history is worth keeping, so a recompute
    // for redelivery alone compares first, as the boot recompute does.
    if (rejected.length > 0 || redelivered) this.#recompute(rejected.length === 0);

    this.#refreshStatus();
    if (this.#pending.length > 0) this.#push();
  }

  /**
   * Fold the server prefix and the outbox back into what the UI shows.
   *
   * Called only when the command *sequence* changed — see the module header on
   * why an ordinary ack must not come through here, and on why `#pending` must
   * already hold commands whose outbox write is still in flight when it does.
   *
   * `ifChanged` is for the one caller that recomputes without knowing that: the
   * first catch-up of a session, which cannot tell a store that already holds
   * this workspace from one an empty hydration left behind. Comparing the two
   * documents costs a serialisation and saves the undo stack on every reload
   * where they already agree. Commands the replay refused are still dropped and
   * reported, because that is true whichever way the comparison goes.
   *
   * `over` is the remote commands this recompute folds in, when they are all
   * of the difference; the store then keeps the undo entries they do not touch.
   * It is ignored the moment the replay drops one of ours.
   */
  #recompute(ifChanged = false, over?: readonly Command[]): void {
    const result = replay(
      this.#confirmed,
      this.#pending.map((entry) => entry.command),
    );

    if (result.dropped.length > 0) {
      const droppedIds = new Set(result.dropped.map((entry) => entry.command.commandId));
      const removed = this.#pending.filter((entry) => droppedIds.has(entry.command.commandId));
      this.#pending = this.#pending.filter((entry) => !droppedIds.has(entry.command.commandId));
      this.#prune(this.#keysOf(removed));
      this.#report(result.dropped);
    }

    if (ifChanged && sameWorkspace(result.state, this.#store.getState())) return;
    this.#store.replaceState(
      result.state,
      over === undefined || result.dropped.length > 0 ? {} : { over },
    );
  }

  /**
   * Split refused commands by whether the *account* refused them.
   *
   * A `plan-limit` rejection is the only one that is not a fault: the command
   * was well formed and the workspace is consistent. Routing it to its own
   * callback is what lets the UI answer with "Trial includes 10 recipes"
   * instead of "an edit could not be synced" (PLAN §9).
   *
   * `recipe-shared` is not a fault either, and not even a refusal: the command
   * went to the room that holds the recipe now. Nothing is shown for it.
   */
  #report(dropped: readonly DroppedCommand[]): void {
    const planLimited = dropped.filter((entry) => entry.error.code === 'plan-limit');
    const shared = dropped.filter((entry) => entry.error.code === 'recipe-shared');
    const rest = dropped.filter(
      (entry) => entry.error.code !== 'plan-limit' && entry.error.code !== 'recipe-shared',
    );
    if (planLimited.length > 0) this.#options.onPlanLimit?.(planLimited);
    if (shared.length > 0) this.#options.onRecipeShared?.();
    if (rest.length > 0) this.#options.onDropped?.(rest);
  }

  /**
   * The catch-up is done: paint it, remember it, and say so.
   *
   * `boot` is the session's first, and it recomputes whether or not the server
   * had anything to add — see the module header for the empty hydration that
   * makes the ordinary `changed` rule a wrong answer on a boot. `#everCaughtUp`
   * is set before `#refreshStatus`, so a subscriber woken by the status change
   * reads the flag it was woken for.
   *
   * `over` is passed through to `#recompute` for a live delta, never on a boot.
   */
  #finishCatchUp(changed: boolean, over?: readonly Command[]): void {
    const boot = !this.#everCaughtUp;
    this.#caughtUp = true;
    this.#everCaughtUp = true;
    if (changed || boot) this.#recompute(boot, boot ? undefined : over);
    this.#rememberPrefix();
    if (boot) {
      void this.#adoptGuestWorkspace().catch((error: unknown) => {
        this.#options.onError?.(error);
      });
    }
    this.#refreshStatus();
    this.#push();
  }

  /**
   * Throw the confirmed prefix away and rebuild it from scratch.
   *
   * `pull(0)` is always answerable — a delta from the very beginning if the log
   * still reaches back that far, a snapshot otherwise — so this is the one
   * recovery path that cannot itself fail. The outbox is untouched and gets
   * replayed on top of whatever comes back.
   */
  /**
   * Note where the prefix has got to, for the next session to resume from.
   *
   * Only while caught up: a prefix recorded mid-catch-up would name a version
   * whose state is half a log behind, which is the shape of bug that made this
   * worth building. The write itself is debounced inside the store.
   */
  #rememberPrefix(): void {
    if (!this.#caughtUp) return;
    this.#confirmedStore?.save({ version: this.#version, state: this.#confirmed });
  }

  #resync(cause: unknown): void {
    if (cause !== null) this.#options.onError?.(cause);
    // The stored prefix goes with the one in memory. Leaving it would hand the
    // next boot the very base this resync exists to abandon.
    void this.#confirmedStore?.clear();
    this.#confirmed = emptyWorkspace();
    this.#version = 0;
    this.#caughtUp = false;
    this.#inFlight = null;
    this.#send(pullMessage(0));
    this.#refreshStatus();
  }

  // -------------------------------------------------------------------------
  // Guest data
  // -------------------------------------------------------------------------

  async #adoptGuestWorkspace(): Promise<void> {
    const read = this.#options.guestWorkspace;
    if (read === undefined) return;
    const guest = await read();
    if (guest === null || isEmptyWorkspace(guest)) return;

    if (this.#version > 0) {
      // Server wins. Guest data stays under its own key, untouched.
      this.#options.onGuestConflict?.(guest);
      return;
    }
    for (const command of uploadCommands(guest, {
      now: this.#options.now ?? Date.now,
      nextCommandId: this.#options.nextCommandId ?? (() => newId('c')),
    })) {
      this.#store.dispatchCommand(command);
    }
  }

  // -------------------------------------------------------------------------
  // Status
  // -------------------------------------------------------------------------

  #refreshStatus(): void {
    this.#setStatus(this.#currentStatus());
  }

  #currentStatus(): SyncStatus {
    if (!this.#started) return 'guest';
    if (this.#stale) return 'stale';
    // No socket: `connecting` while one is being opened, `offline` once a retry
    // has been scheduled — the difference the user cares about is whether the
    // app has given up for the moment, not whether a TCP handshake is in flight.
    if (!this.#open) return this.#reconnectTimer === null ? 'connecting' : 'offline';
    if (!this.#caughtUp || this.#pending.length > 0 || this.#inFlight !== null) return 'syncing';
    return 'synced';
  }

  /**
   * Publish the status, and the queue length with it.
   *
   * The count is part of what the indicator shows — "Syncing · 3" is how a
   * brewer can tell a queue that is moving from one that is not — so a push
   * that settles three commands out of five has to reach the subscribers even
   * though the status is `syncing` before and after. `#refreshStatus` already
   * runs at every point the queue changes, which is why comparing the count
   * here is enough.
   *
   * {@link SyncClient.hasCaughtUp} rides the same subscription for the same
   * reason. A catch-up that lands with commands still queued is `syncing` on
   * both sides of itself and moves no count, so without this the one fact an id
   * page is waiting for would be published only by whatever happened next.
   */
  #setStatus(status: SyncStatus): void {
    const pending = this.#pending.length;
    const caughtUp = this.#everCaughtUp;
    if (
      this.#status === status &&
      this.#notifiedPending === pending &&
      this.#notifiedCaughtUp === caughtUp
    ) {
      return;
    }
    this.#status = status;
    this.#notifiedPending = pending;
    this.#notifiedCaughtUp = caughtUp;
    for (const listener of this.#statusListeners) listener();
  }

  #clearTimer(which: 'reconnect' | 'push' | 'deadline' | 'heartbeat' | 'presence'): void {
    if (which === 'reconnect' && this.#reconnectTimer !== null) {
      clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
    }
    if (which === 'push' && this.#pushTimer !== null) {
      clearTimeout(this.#pushTimer);
      this.#pushTimer = null;
    }
    if (which === 'deadline' && this.#deadlineTimer !== null) {
      clearTimeout(this.#deadlineTimer);
      this.#deadlineTimer = null;
      this.#deadlineArmedAt = null;
    }
    if (which === 'heartbeat' && this.#heartbeatTimer !== null) {
      clearInterval(this.#heartbeatTimer);
      this.#heartbeatTimer = null;
    }
    if (which === 'presence' && this.#presenceTimer !== null) {
      clearTimeout(this.#presenceTimer);
      this.#presenceTimer = null;
    }
  }
}

/**
 * The command batch that uploads a whole workspace.
 *
 * `restore` rather than `create`: it is an upsert that carries the document
 * verbatim, timestamps included, so uploading is lossless and re-uploading is
 * idempotent. Built-in equipment profiles are skipped — the server workspace
 * already has them, and restoring them would only churn versions.
 */
export function uploadCommands(
  workspace: WorkspaceState,
  clock: { now: () => number; nextCommandId: () => string },
): readonly Command[] {
  const commands: Command[] = [];
  const envelope = () => ({
    commandId: clock.nextCommandId(),
    timestamp: clock.now(),
    actor: 'user' as const,
  });

  for (const profile of Object.values(workspace.equipmentProfiles)) {
    if (profile.builtin) continue;
    commands.push(makeCommand('equipment.restore', { profile }, envelope()));
  }
  for (const recipe of Object.values(workspace.recipes)) {
    commands.push(makeCommand('recipe.restore', { recipe }, envelope()));
  }
  return commands;
}
